import { getLocalization } from 'global/global';
import { LocationHierarchyInterface } from 'Interfaces/HierarchyInterface';
import { StateInterface } from 'Interfaces/StateInterface';
import * as React from 'react';
import { Button, Form, FormGroup } from 'react-bootstrap';
import { connect } from 'react-redux';
import GenericModal from 'views/Modals/GenericModal';
import Papa from 'papaparse';
import X2JS from 'x2js';
import { JSONInterface } from 'Interfaces/JsonInterface';
import { generateID } from 'views/LocationHierarchy/utils';
import { saveHierarchies } from 'actions/hierarchyActions';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'typescript-fsa';
// import { toast } from 'react-toastify';

interface Props {
  addHierarchyFromJSON: (json: JSONInterface) => void;
}

interface StateProps {
  locationHierarchies: LocationHierarchyInterface[];
}

interface ActionProps {
  saveHierarchies: (hierarchies) => Promise<JSONInterface>;
}

type ImportPostalCodesProps = Props & StateProps & ActionProps;

const ImportHierarchy = (props: ImportPostalCodesProps) => {

  const [importVisible, setImportVisible] = React.useState(false);
  const [fileName, setFileName] = React.useState(getLocalization('noFileSelected'));
  const [file, setFile] = React.useState('');
  const pcInput = React.useRef<HTMLInputElement>(null);
  const [lh, setLh] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [sameLocationChecked, setSameLocationChecked] = React.useState(false);

  const onImportLocationHierarchyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    let i = 0;
    if (files) {
      while (i < files?.length) {
        const f = files[i];
        if (!(f.type.match('text/csv') || f.type.match('text/plain') || f.type.match(''))) {
          return;
        }
        const reader = new FileReader();
        reader.onload = ((theFile) => {
          console.log(theFile);
          return (fe) => {
            if (fe) {
              const text = fe.target?.result;
              if (text && typeof text === 'string') {
                setFile(`${text}`);
                setFileName(f.name);
              }
            }
          };
        })(f);
        reader.readAsText(f);
        i++;
      }
    }
    e.target.value = '';
  };

  const getCode = (value, parentCode) => {
    if (value < 10) {
      value = `00` + value;
    } else if (value < 100) {
      value = `0` + value;
    }

    if (!parentCode) {
      return value;
    } else {
      return parentCode += value;
    }
  };

  /*
    Copyright (c) 2011 Andrei Mackenzie
    Permission is hereby granted, free of charge, to any person obtaining a copy of this software and
    associated documentation files (the "Software"), to deal in the Software without restriction, including without
    limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
    Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
    The above copyright notice and this permission notice shall be included in all copies or substantial portions of
    the Software.
    THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
    THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
    AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
    TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
    DEALINGS IN THE SOFTWARE.
  */

  // Compute the edit distance between the two given strings
  const getEditDistance = (a: string, b: string) => {
    if (a.length === 0) {
      return b.length;
    }
    if (b.length === 0) {
      return a.length;
    }

    const matrix: number[][] = [];

    // increment along the first column of each row
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }

    // increment each column in the first row
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, // substitution
            Math.min(matrix[i][j - 1] + 1, // insertion
              matrix[i - 1][j] + 1)); // deletion
        }
      }
    }
    return matrix[b.length][a.length];
  };

  const processImport = () => {
    const selected = props.locationHierarchies.find(l => `${l.id}` === lh);
    let hierarchy = {
      LocationHierarchy: {
        _name: newName,
        _ID: generateID(),
        LocationNames: {}
      }
    };
    if (selected && selected.xml) {
      const x2js = new X2JS();
      const jsonObj = x2js.xml2js(selected.xml);
      if (jsonObj) {
        // @ts-ignore
        const lhObject = jsonObj['Document'];
        if (lhObject && lhObject.LocationHierarchy) {
          hierarchy = lhObject;
        }
      }
    }
    const results = Papa.parse(file.trim());
    const data = results.data;
    let first = true;
    for (const d of data) {
      if (first) {
        const locationNames = {};
        d.forEach((name, index) => {
          locationNames[`Location${index + 1}`] = name;
        });
        first = false;
        hierarchy.LocationHierarchy.LocationNames = locationNames;
      } else {
        let current: JSONInterface = hierarchy.LocationHierarchy;
        let location: JSONInterface | null = null;
        d.forEach((name, index) => {
          if (name !== '') {
            if (!current[`Location${index + 1}Question`]) {
              current[`Location${index + 1}Question`] = [];
            } else if (current[`Location${index + 1}Question`].length === undefined) {
              // The location is there but it is a JSON Object
              const temp = current[`Location${index + 1}Question`];
              current[`Location${index + 1}Question`] = [];
              current[`Location${index + 1}Question`].push(temp);
            }
            if (current[`Location${index + 1}Question`].length > 0) {
              const locs = current[`Location${index + 1}Question`].filter(l => {
                return l.__text?.trim() === name.trim();
              });
              if (locs.length > 0) {
                location =  locs[0];
              } else {
                location = null;
              }
            }
            if (location === null) {
              let addNew = true;
              if (sameLocationChecked) {
                current[`Location${index + 1}Question`].forEach(loc => {
                  if (loc.__text.trim() === name.trim() || getEditDistance(loc.__text?.trim(), name.trim()) === 1) {
                    addNew = false;
                    location = loc;
                  }
                });
              }
              if (addNew) {
                location = {};
                location._ID = generateID();
                location.__text = name.trim();
                location._code = getCode(current[`Location${index + 1}Question`].length + 1, current._code);
                current[`Location${index + 1}Question`].push(location);
              }
            }
            if (location) {
              current = location;
            }
            location = null;
          }
        });
      }
    }
    console.log(hierarchy);
    props.addHierarchyFromJSON(hierarchy.LocationHierarchy);
    setImportVisible(false);
  };


  const getBody = () => {
    return (
      <>
        <FormGroup>
          <Form.Label>
            {getLocalization('selectHierarchy')}
          </Form.Label>
          <Form.Control
            as={'select'}
            size={'sm'}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              setLh(e.target.value);
              setSameLocationChecked(false);
              setNewName('');
            }}
          >
            <option value={''}>{getLocalization('selectOne')}</option>
            {props.locationHierarchies.map(lh => {
              return (
                <option key={`import-lh-${lh.id}`} value={lh.id}>{lh.name}</option>
              );
            })}
          </Form.Control>
        </FormGroup>
        <Form.Group>
          <Form.Label>
            {getLocalization('nameNewHierarchy')}
          </Form.Label>
          <Form.Control
            type={'text'}
            size={'sm'}
            value={newName}
            disabled={lh !== ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Text>
            {getLocalization('hierarchyImportFile')}
          </Form.Text>
        </Form.Group>
        <Form.Group>
          <Button
            size={'sm'}
            onClick={() => {
              if (pcInput.current) {
                pcInput.current.click();
              }
            }}
          >
            <input
              ref={pcInput}
              className={'d-none'}
              onChange={onImportLocationHierarchyChange}
              type={'file'}
              accept={'.csv, .txt, plain/text, text/csv'}
            />
            {getLocalization('selectFile')}
          </Button>
          <span>{fileName}</span>
        </Form.Group>
        {lh !== '' && (
          <Form.Group>
            <Form.Check
              type="checkbox"
              label={getLocalization('sameLocationCheck')}
              checked={sameLocationChecked}
              onChange={() => setSameLocationChecked(sameLocationChecked)}
            />
          </Form.Group>
        )}
      </>
    );
  };

  return (
    <>
      {importVisible && (
        <GenericModal
          visible={importVisible}
          title={getLocalization('modalImportHierarchy')}
          body={getBody()}
          onConfirm={() => {
            processImport();
          }}
          confirmText={getLocalization('import')}
          cancelText={getLocalization('close')}
          cancel={() => setImportVisible(false)}
        />
      )}
      <Button
        size={'sm'}
        onClick={() => setImportVisible(true)}
      >
        {getLocalization('importHierarchy')}
      </Button>
    </>
  );
};

const mapStateToProps = (state: StateInterface): StateProps => {
  return {
    locationHierarchies: state.locationHierarchies.locationHierarchies,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<StateInterface, any, AnyAction>): ActionProps => {
  return {
    saveHierarchies: (hierarchies) => dispatch(saveHierarchies(hierarchies))
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(ImportHierarchy);
